import React from 'react';

import { Heading, Subheading } from '@/components-new/heading';
import { Seo } from '@/components-new/seo';
import { Strong, Text } from '@/components-new/text';

const Section = ({ title, children }: { title: string, children: React.ReactNode }) => (
  <section className="mt-8 space-y-3">
    <Subheading>{title}</Subheading>
    {children}
  </section>
);

const List = ({ children }: { children: React.ReactNode }) => (
  <ul className="list-disc space-y-2 pl-6 text-base/6 text-zinc-500 dark:text-zinc-400 sm:text-sm/6">
    {children}
  </ul>
);

const TermsOfUsePage = () => {
  return (
    <div className="min-h-full bg-gray-50 px-6 py-12 lg:px-8">
      <Seo title="Terms of Use"/>
      <div className="mx-auto max-w-3xl">
        <img
          alt="Artia Capsule Logo"
          src="/artia-capsule-logo.png"
          className="h-16 w-auto"
        />
        <Heading className="mt-8">Artia Solutions Platform Terms of Use</Heading>
        <Text className="mt-2">Last Updated: October 2026</Text>

        <div className="mt-8 space-y-3">
          <Text>
            These Platform Terms of Use (these “Terms”), between Artia Solutions, LLC (“Artia,” “we,” or “us”)
            and the entity you represent (“Client” or “you”), govern access to and use of Capsule and any other
            proprietary Artia applications, portals, dashboards, tools, or databases made available by Artia
            through a web browser, mobile application, API, or other means (collectively, the “Platform”).
          </Text>
          <Text>
            Access to the Platform and/or other Artia services may be provided for under a separate written
            agreement — for example, a master services agreement, statement of work, or order form
            (together with all exhibits and amendments, the “Client Agreement”). These Terms supplement,
            and do not replace, the Client Agreement, and apply to access the Platform. If you are accessing
            the Platform on behalf of a Client, you represent that you are authorized to do so and, you
            acknowledge that you are bound by both these Terms and the applicable Client Agreement.
          </Text>
        </div>

        <Section title="1. Definitions">
          <List>
            <li>
              <Strong>“Artia Materials”</Strong> means all software, applications, source code, models, methodologies,
              algorithms, data compilations, databases, templates, reports, analyses, visualizations,
              benchmarks, documentation, Artia Background IP, and other content underlying the Platform
              made available by or on behalf of Artia, in any format.
            </li>
            <li>
              <Strong>“Artia Background IP”</Strong> means all know-how, processes, methodologies, tools, data assets,
              and other intellectual property owned or licensed by Artia prior to or independent of any
              engagement with Client.
            </li>
            <li>
              <Strong>“Output(s)”</Strong> means any report, analysis, export, download, screen display, extract, summary,
              or other result generated by or obtained from the Platform.
            </li>
            <li>
              <Strong>“Authorized User”</Strong> means a named individual employee or contractor of Client who is
              authorized by Client, permitted by Artia, and provided with unique credentials or authorization
              to access the Platform solely on Client’s behalf and subject to these Terms.
            </li>
            <li>
              <Strong>“Restricted Party”</Strong> means any person or entity that (a) competes with Artia or offers
              products or services substantially similar to any Artia offering, and (b) any consultant, agency,
              vendor, or other third party engaged by or acting for such a person or entity, in each case
              whether or not engaged by Client.
            </li>
          </List>
        </Section>

        <Section title="2. Access Rights">
          <Text>
            Subject to these Terms and payment of applicable fees, Artia grants Client a limited, non-exclusive,
            non-transferable, non-sublicensable, revocable (with respect to Artia Materials) right for
            Authorized Users to access and use the Platform and Outputs solely for Client’s internal business
            purposes during the applicable subscription or engagement term. Artia reserves all rights not
            expressly granted. No title to, or ownership of, any Artia Materials, the Platform or Artia
            Background IP is transferred to Client.
          </Text>
          <Text>
            Client is responsible for all activity occurring under its accounts and for all acts and omissions of its
            Authorized Users. Credentials are personal to each Authorized User and must not be shared, sold,
            or transferred. Client shall promptly notify Artia of any unauthorized access or use.
          </Text>
        </Section>

        <Section title="3. Restrictions on Disclosure and Sharing">
          <Text>
            Client acknowledges that Artia Materials reflect years of specialized expertise and substantial
            investment by Artia, derive independent economic value from not being generally known, and
            constitute Artia’s confidential information and trade secrets. Accordingly, without Artia’s written
            consent, Client shall not, and shall not permit any Authorized User or third party to:
          </Text>
          <List>
            <li>
              Disclose, publish, distribute, display, transmit, license, sublicense, sell, resell, rent, lease, or
              otherwise make available Artia Materials or associated Outputs, in whole or in part, to any
              third party;
            </li>
            <li>
              Provide, disclose, or make accessible any Artia Materials or associated Output to any
              Restricted Party, including by providing access to a Restricted Party retained by Client as a
              consultant, advisor, or service provider;
            </li>
            <li>Permit access to the Platform by any person who is not an Authorized User;</li>
            <li>
              Incorporate any Artia Materials or Output into any deliverable, work product, publication,
              presentation, or submission provided to a third party, except that Client may share Output
              internally and with its representatives who have a need to know, are bound by written
              confidentiality obligations at least as protective as these Terms, and who are not Restricted
              Parties; or
            </li>
            <li>
              Remove, alter, or obscure any proprietary rights notice, watermark, attribution, or
              confidentiality legend contained in or on any Artia Materials or Output.
            </li>
          </List>
          <Text>
            Client shall promptly notify Artia in writing upon becoming aware of any actual or suspected
            unauthorized access, use, or disclosure.
          </Text>
        </Section>

        <Section title="4. Additional Use Restrictions">
          <Text>Client shall not, and shall not permit any Authorized User or third party to:</Text>
          <List>
            <li>
              Copy, modify, translate, or create derivative works of the Platform or any Artia Materials,
              except for a reasonable number of copies of Outputs for Client’s internal use;
            </li>
            <li>
              Reverse engineer, decompile, disassemble, or otherwise attempt to derive the source code,
              structure, models, methodologies, data sources, weights, or underlying know-how of the
              Platform;
            </li>
            <li>
              Access or use the Platform or Artia Materials, to develop, train, benchmark, validate, market,
              or improve any product, service, dataset, model, or artificial intelligence or machine learning
              system or to assist any third party in doing so;
            </li>
            <li>
              Use any robot, spider, scraper, crawler, script, bot, or other automated or manual means to
              access, monitor, extract, harvest, index, or copy any portion of the Platform or Artia Materials,
              or to circumvent any access control, rate limit, or usage restriction;
            </li>
            <li>
              Use the Platform in a manner that disables, overburdens, damages, or impairs the Platform
              or interferes with any other party’s use, or introduce any virus, malware, or other harmful
              code;
            </li>
            <li>
              Perform, publish, or disclose any competitive analysis, benchmark test, or performance
              comparison of the Platform; or
            </li>
            <li>
              Use the Platform or Output in violation of any applicable law, regulation, or third-party right, or
              for any purpose other than Client’s internal business purposes.
            </li>
          </List>
        </Section>

        <Section title="5. Ownership; Feedback">
          <Text>
            As between the parties, Artia exclusively owns all right, title, and interest in and to the Platform and
            Artia Materials, together with all derivatives, enhancements, and improvements thereof. Client
            owns its own data that it or any Authorized User submits or inputs into the Platform (“Input(s)”) and
            grants Artia a non-exclusive, worldwide, royalty-free license to host, process, and use Inputs solely
            as necessary to provide, operate, secure, support, and improve the Platform. Artia may compile
            and use aggregated and de-identified data and usage statistics that do not identify Client, Inputs,
            or contain Client confidential information, and Artia exclusively owns such aggregated and
            de-identified data. For the avoidance of doubt, subject to Sections 2, 3, and 4, Client owns all right,
            title, and interest in and to Outputs.
          </Text>
          <Text>
            If Client or any Authorized User provides suggestions, comments, or other feedback regarding the
            Platform (“Feedback”), Client hereby assigns all right, title, and interest in such Feedback to Artia,
            and Artia may use Feedback for any purpose without restriction, attribution, or compensation.
          </Text>
        </Section>

        <Section title="6. Confidentiality">
          <Text>
            The Artia Materials and all non-public information regarding the Platform are Artia’s confidential
            information. Client shall (a) use such information solely as permitted by these Terms; (b) protect it
            using at least the same degree of care it uses for its own confidential information of like
            importance, and in no event less than a reasonable degree of care; and (c) limit access to those
            Authorized Users and its representatives with a need to know who are bound by written obligations
            no less protective than these Terms. Client’s obligations under this Section survive termination and
            continue for so long as the information remains confidential or, with respect to trade secrets, for so
            long as the information qualifies as a trade secret under applicable law. If Client is compelled by
            law or legal process to disclose any Artia confidential information, Client shall, to the extent legally
            permitted, provide prompt prior written notice to Artia and reasonable cooperation, at Artia’s
            expense, so that Artia may seek a protective order or other appropriate remedy.
          </Text>
        </Section>

        <Section title="7. Monitoring, Suspension, and Termination">
          <Text>
            Artia may monitor use of the Platform to verify compliance with these Terms and to protect the
            security and integrity of the Platform. Artia may suspend or terminate Client’s or any Authorized
            User’s access, in whole or in part, immediately and without liability, if Artia reasonably believes that
            (a) these Terms have been violated; (b) access presents a security, legal, or reputational risk; or
            (c) required fees have not been paid. Upon expiration or termination of a Client Agreement, Artia
            may suspend Client’s or any Authorized User’s access to the Platform and Client shall return or
            destroy all copies of Artia Materials in its possession or control, except for copies retained in
            routine backup systems or as required by law, which remain subject to these Terms.
          </Text>
        </Section>

        <Section title="8. Equitable Relief">
          <Text>
            Client acknowledges that a breach or threatened breach of Sections 3, 4, or 6 would cause Artia
            irreparable harm for which monetary damages would be an inadequate remedy. Accordingly, Artia
            is entitled to seek equitable relief, including injunctive relief and specific performance, without any
            requirement to post a bond or other security or to prove actual damages, in addition to all other
            remedies available at law or in equity.
          </Text>
        </Section>

        <Section title="9. Disclaimers">
          <Text>
            THE PLATFORM, ARTIA MATERIALS, AND OUTPUT ARE PROVIDED “AS IS” AND “AS
            AVAILABLE.” ARTIA DISCLAIMS ALL WARRANTIES, WHETHER EXPRESS, IMPLIED,
            STATUTORY, OR OTHERWISE, INCLUDING THE IMPLIED WARRANTIES OF
            MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT,
            AND ANY WARRANTIES ARISING FROM COURSE OF DEALING, USAGE,
            OR TRADE PRACTICE. ARTIA DOES NOT WARRANT THAT THE PLATFORM WILL BE
            UNINTERRUPTED, ERROR-FREE, OR SECURE, OR THAT ANY OUTPUT WILL BE ENTIRELY
            ACCURATE OR COMPLETE. OUTPUTS ARE PROVIDED FOR INFORMATIONAL PURPOSES
            ONLY, DOES NOT CONSTITUTE LEGAL, REGULATORY, MEDICAL, OR REIMBURSEMENT
            ADVICE, AND CLIENT IS SOLELY RESPONSIBLE FOR ITS DECISIONS AND ACTIONS TAKEN
            IN RELIANCE ON ANY OUTPUT.
          </Text>
        </Section>

        <Section title="10. Indemnification">
          <Text>
            Client shall defend, indemnify, and hold harmless Artia and its affiliates, and their respective
            officers, directors, employees, and agents, from and against any third party claims, damages,
            losses, liabilities, costs, and expenses (including reasonable attorneys’ fees) arising out of or
            relating to (a) Client’s or any Authorized User’s breach of these Terms; (b) any unauthorized
            access to, use, or disclosure of the Artia Materials resulting from Client’s acts or omissions; or (c)
            Inputs or Client’s use of Outputs.
          </Text>
        </Section>

        <Section title="11. Limitation of Liability">
          <Text>
            To the maximum extent permitted by law, neither party will be liable for any indirect, incidental,
            consequential, special, exemplary, or punitive damages, or for lost profits, revenue, data, or
            goodwill, regardless of the theory of liability, even if advised of the possibility of such damages.
            The total aggregate liability arising out of or relating to these terms will not exceed the amounts
            paid by Client to Artia for access to the Platform during the twelve (12) months preceding the event
            giving rise to the claim. The limitations in this section do not apply to client’s obligations under
            sections 3, 4, or 6.
          </Text>
        </Section>

        <Section title="12. Changes to These Terms">
          <Text>
            Artia may modify these Terms from time to time. Artia will post the revised Terms with an updated
            “Last Updated” date and, for material changes, may provide notice through the Platform or by
            email. Continued access to or use of the Platform after the effective date of the revised Terms
            constitutes acceptance of the changes. If Client does not agree, Client must stop using the
            Platform.
          </Text>
        </Section>

        <Section title="13. General">
          <Text>
            <Strong>Links to Third Party Sites.</Strong> The Platform may contain links to third-party websites or resources
            (collectively, “Third-Party Services”). Third-Party Services are not owned, operated, or controlled
            by Artia, and Artia is not responsible for, and makes no representations or warranties regarding the
            availability, accuracy, legality, security, content, products, services, or practices of any Third-Party
            Service. Artia is not responsible for any changes, updates, interruptions, failures, transmissions, or
            communications associated with any Third-Party Service. Any access to or use of a Third-Party
            Service through the Platform is solely between you and the applicable third party and is governed
            by that third party’s terms and policies. Artia may provide access through the Platform to Third-Party
            Services solely as a convenience and does not imply Artia’s endorsement of, affiliation with,
            sponsorship of, or association with the applicable third party. Artia shall not be liable for any loss,
            damage, or claim arising from or related to your/Client’s access to or use of any Third-Party
            Service.
          </Text>
          <Text>
            <Strong>Governing Law; Venue.</Strong> These Terms are governed by the laws of the State of New York, without
            regard to conflict-of-laws principles. The parties submit to the exclusive jurisdiction of the state and
            federal courts located in New York and waive any objection to venue or forum non conveniens.
          </Text>
          <Text>
            <Strong>Assignment.</Strong> Client may not assign or transfer these Terms or any rights hereunder without Artia’s
            prior written consent. Any attempted assignment in violation of this Section is void.
          </Text>
          <Text>
            <Strong>Severability; Waiver.</Strong> If any provision is held unenforceable, it will be modified to the minimum
            extent necessary to make it enforceable, and the remaining provisions will remain in full force. No
            failure or delay in exercising any right operates as a waiver.
          </Text>
          <Text>
            <Strong>Survival.</Strong> Sections 1, 3, 4, 5, 6, 8, 9, 10, 11, and 13 survive any expiration or termination of these
            Terms or of Client’s access to the Platform.
          </Text>
          <Text>
            <Strong>Entire Agreement; Conflicts; Scope.</Strong> These Terms, together with any applicable Client
            Agreement, constitute the entire agreement regarding the Platform and supersede all prior or
            contemporaneous understandings with respect to the subject matter hereto. In the event of a direct
            conflict between these Terms and the applicable Client Agreement with respect to subject matter
            hereof, the Client Agreement controls as to the given subject matter, except that (a) Sections 3
            (Restrictions on Disclosure and Sharing), 4 (Additional Use Restrictions), and 5 (Ownership) of
            these Terms apply in addition to, and are not limited or superseded by, any provision of the Client
            Agreement; and (b) where these Terms and the Client Agreement both address a subject and can
            be read together, they will be construed as cumulative and given their broadest consistent effect.
            No provision of these Terms will be deemed to conflict with the Client Agreement merely because it
            is more specific, more protective of Artia, or addresses a subject the Client Agreement does not
            address. The scope of access, the specific applications and services made available, the number
            and identity of Authorized Users, the term, and all fees are governed exclusively by the applicable
            Client Agreement. These Terms do not grant any right of access absent a current, effective Client
            Agreement, and access rights terminate automatically upon expiration or termination of the Client
            Agreement or the applicable statement of work.
          </Text>
          <Text>
            <Strong>Contact.</Strong> Questions regarding these Terms, or requests for consent to any use not expressly
            permitted, should be directed to your Artia point of contact for your account.
          </Text>
        </Section>
        <div className="pt-12 text-center text-gray-500">
          &copy; {new Date().getFullYear()} by Artia Solutions, LLC
        </div>
      </div>
    </div>
  );
};

export default TermsOfUsePage;
